/**
 * Extract Twilio error codes for a rejected submission (CX-3086) so each one
 * can link to https://www.twilio.com/docs/api/errors/<code>.
 *
 * Codes come from the stored rejection text ("30497: ...", written by the
 * backend) and, for rows saved before codes were kept in the text, from the
 * raw rejectionReasons / errorCode in twilio_response.
 */

export const twilioErrorDocsUrl = (code) => `https://www.twilio.com/docs/api/errors/${code}`;

// Twilio messaging/verification error codes are 5 digits (30xxx, 21xxx, ...)
const CODE_IN_TEXT = /\b([1-9]\d{4})\b/g;
const isCode = (value) => /^[1-9]\d{4}$/.test(String(value ?? '').trim());

const parseResponse = (resp) => {
  if (!resp) return null;
  if (typeof resp === 'object') return resp;
  try {
    return JSON.parse(resp);
  } catch {
    return null;
  }
};

export function getRejectionErrorCodes(submission) {
  if (!submission) return [];
  const codes = new Set();

  const text = submission.twilio_rejection_reason || '';
  for (const match of text.matchAll(CODE_IN_TEXT)) codes.add(match[1]);

  // twilio_response can keep an old rejection after a resubmit, so only trust
  // it while the submission is currently rejected.
  if (submission.twilio_status !== 'TWILIO_REJECTED') return [...codes];

  const response = parseResponse(submission.twilio_response);
  const reasons = response?.rejectionReasons ?? response?.rejection_reasons;
  if (Array.isArray(reasons)) {
    reasons.forEach((r) => {
      const code = r && typeof r === 'object' ? (r.code ?? r.error_code ?? r.errorCode) : null;
      if (isCode(code)) codes.add(String(code).trim());
    });
  }
  if (isCode(response?.errorCode)) codes.add(String(response.errorCode).trim());

  return [...codes];
}
